import {
  cloneElement,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { createPortal } from 'react-dom'
import { cx, isFilled, PortalThemeContext } from '@/shared/lib'
import s from './Tooltip.module.css'

/** Две ступени кита: S — компактная плашка под иконку, L — блок под термин с заголовком. */
export type TooltipSize = 'xs' | 'sm'

export type TooltipPlacement = 'top' | 'right' | 'bottom' | 'left'

/** Выравнивание вдоль триггера. Работает у `top`/`bottom`; сбоку плашка центрируется. */
export type TooltipAlign = 'center' | 'start' | 'end'

/**
 * `hover` — наведение и фокус: на тач-экране подсказка не показывается вовсе,
 * потому что касание боевой кнопки обязано делать её работу, а не открывать плашку.
 * `press` — нажатие: для триггеров, единственная работа которых — показать подсказку.
 */
export type TooltipTrigger = 'hover' | 'press'

type TooltipOwnProps = {
  /** Текст подсказки. Пустой — подсказки нет: встроенный «?» не рисуется, триггер идёт как есть. */
  content?: ReactNode
  /** Заголовок плашки: термин, под ним объяснение. Рассчитан на ступень `sm`. */
  title?: string
  size?: TooltipSize
  placement?: TooltipPlacement
  align?: TooltipAlign
  trigger?: TooltipTrigger
  /** Управляемый режим. Не задан — состояние держит компонент. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Задержка появления при наведении, мс. Фокус и нажатие открывают немедленно. */
  openDelay?: number
  /** Задержка скрытия после ухода курсора, мс. */
  closeDelay?: number
  /** Подсказка дублирует доступное имя триггера — плашка уходит из дерева доступности. */
  decorative?: boolean
  ref?: Ref<HTMLSpanElement>
} & Omit<HTMLAttributes<HTMLSpanElement>, 'content' | 'title' | 'children'>

/**
 * Без своего триггера подсказку некому вызвать, а встроенной кнопке «?» нужно доступное имя:
 * требование выражено типом, а не абзацем в документации.
 */
export type TooltipProps = TooltipOwnProps &
  ({ children: ReactNode; label?: string } | { children?: undefined; label: string })

/** Единственное, что компонент дописывает чужому триггеру. */
type TriggerProps = { 'aria-describedby'?: string }

// Карты классов при noUncheckedIndexedAccess приходят как string | undefined
const SIZE_CLASS: Record<TooltipSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
}

const PLACEMENT_CLASS: Record<TooltipPlacement, string | undefined> = {
  top: s.top,
  right: s.right,
  bottom: s.bottom,
  left: s.left,
}

/** Противоположная сторона: единственное правило переворота, больше кит не требует. */
const OPPOSITE: Record<TooltipPlacement, TooltipPlacement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

/**
 * Отступ от края экрана при прижатии. Зеркалит --space-4 — тот же отступ, которым зажата
 * max-width плашки, поэтому у прижатой подсказки поля с двух сторон одинаковы. Живёт
 * константой, а не токеном: участвует в арифметике координат, а читать getComputedStyle
 * на каждый кадр прокрутки — плохой обмен (довод из Popover).
 */
const VIEWPORT_MARGIN = 16

/** Отступ стрелки от угла плашки: та же ступень, и стрелка не заезжает на скругление (r14). */
const ARROW_INSET = 16

type TooltipPosition = {
  top: number
  left: number
  /** Сторона, на которой плашка оказалась в итоге: уезжает в data-side. */
  side: TooltipPlacement
  /** Смещение стрелки вдоль плашки к центру триггера: уезжает в --tooltip-arrow-offset. */
  arrow: number
}

/** Инлайновый стиль слоя: координаты и смещение стрелки — величины вычисленные, не дизайнерские. */
type LayerStyle = CSSProperties & Record<`--${string}`, string>

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high)

/** Субпиксельный дребезг getBoundingClientRect не должен гонять рендер на каждом кадре. */
const round = (value: number): number => Math.round(value * 100) / 100

/**
 * Координаты плашки в системе окна (`position: fixed`). Переворот и прижатие к краям —
 * те же два правила, что у Popover; зазор до триггера в арифметике не участвует, он живёт
 * внутренним отступом самого слоя и уже входит в его габарит.
 */
function computePosition(
  anchor: HTMLElement,
  layer: HTMLElement,
  placement: TooltipPlacement,
  align: TooltipAlign,
): TooltipPosition {
  const rect = anchor.getBoundingClientRect()
  /*
   * clientWidth/clientHeight, а не innerWidth/innerHeight: полоса прокрутки в видимую область
   * не входит. Запасной вариант — для сред без раскладки (jsdom), где оба нулевые.
   */
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth
  const viewportHeight = document.documentElement.clientHeight || window.innerHeight

  const width = layer.offsetWidth
  const height = layer.offsetHeight
  const vertical = placement === 'top' || placement === 'bottom'

  const space: Record<TooltipPlacement, number> = {
    top: rect.top - VIEWPORT_MARGIN,
    bottom: viewportHeight - rect.bottom - VIEWPORT_MARGIN,
    left: rect.left - VIEWPORT_MARGIN,
    right: viewportWidth - rect.right - VIEWPORT_MARGIN,
  }

  const other = OPPOSITE[placement]
  const need = vertical ? height : width
  // Переворот только когда плашка не влезла и на другой стороне места действительно больше
  const side = need <= space[placement] || space[other] <= space[placement] ? placement : other

  const alignedCross = vertical
    ? align === 'start'
      ? rect.left
      : align === 'end'
        ? rect.right - width
        : rect.left + (rect.width - width) / 2
    : // Сбоку плашка центрируется по вертикали: align там не работает — граница из контракта
      rect.top + (rect.height - height) / 2

  const alignedMain =
    side === 'top'
      ? rect.top - height
      : side === 'bottom'
        ? rect.bottom
        : side === 'left'
          ? rect.left - width
          : rect.right

  const left = clamp(
    vertical ? alignedCross : alignedMain,
    VIEWPORT_MARGIN,
    Math.max(VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN),
  )
  const top = clamp(
    vertical ? alignedMain : alignedCross,
    VIEWPORT_MARGIN,
    Math.max(VIEWPORT_MARGIN, viewportHeight - height - VIEWPORT_MARGIN),
  )

  /*
   * Стрелка указывает на центр триггера, а не на середину плашки: после прижатия к краю
   * и при align="start"/"end" это разные точки. По оси стрелки у слоя нет отступа-моста,
   * поэтому начало отсчёта — сам край слоя.
   */
  const anchorCenter = vertical ? rect.left + rect.width / 2 : rect.top + rect.height / 2
  const crossSize = vertical ? width : height
  const arrowMax = crossSize - ARROW_INSET
  const arrow =
    // Плашка короче двух отступов (ступень xs сбоку от иконки) — угол ей занимать нечем,
    // и стрелка честно встаёт по центру, а не в навязанные 16px
    ARROW_INSET > arrowMax
      ? crossSize / 2
      : clamp(anchorCenter - (vertical ? left : top), ARROW_INSET, arrowMax)

  return { top: round(top), left: round(left), side, arrow: round(arrow) }
}

const samePosition = (a: TooltipPosition | null, b: TooltipPosition): boolean =>
  a !== null && a.top === b.top && a.left === b.left && a.side === b.side && a.arrow === b.arrow

/**
 * Подсказка, дополняющая уже видимый элемент: расшифровка термина, правило расчёта,
 * полный текст обрезанного значения. Всегда описание (`aria-describedby`) и никогда имя:
 * доступное имя кнопки-иконки живёт в её собственном `label`.
 *
 * Позиционируется порталом в body и `position: fixed` — тем же движком, что у `Popover`:
 * китовым `absolute` от обёртки плашку режет `overflow` первого же предка (ячейка таблицы,
 * прокручиваемый список разделов сайдбара, панель поповера). Разбор решений (тач, клавиатура,
 * нативный `title`, цена портала, граница с Popover): docs/ui-kit/contracts/tooltip.md
 */
export function Tooltip({
  content,
  title,
  size = 'xs',
  placement = 'top',
  align = 'center',
  trigger,
  open: openProp,
  onOpenChange,
  openDelay = 200,
  closeDelay = 120,
  decorative = false,
  label,
  className,
  children,
  ref,
  ...rest
}: TooltipProps) {
  // Хук — до ранних return: тема экрана, из которого открыт портал (вход — тёмный)
  const portalTheme = useContext(PortalThemeContext)

  const tooltipId = useId()
  const wrapperRef = useRef<HTMLSpanElement | null>(null)
  const layerRef = useRef<HTMLSpanElement | null>(null)
  const timerRef = useRef<number | undefined>(undefined)
  const [internalOpen, setInternalOpen] = useState(false)
  const [position, setPosition] = useState<TooltipPosition | null>(null)

  const open = openProp ?? internalOpen
  const openRef = useRef(open)

  // Держит ref в согласии с отрисованным состоянием — в том числе если потребитель
  // переключится с управляемого режима на неуправляемый
  useEffect(() => {
    openRef.current = open
  }, [open])

  const setOpenState = useCallback(
    (next: boolean) => {
      // В управляемом режиме сверяемся с пропом, а не с последним намерением: потребитель
      // вправе игнорировать просьбу, и вторая такая же просьба обязана до него дойти
      if (openProp !== undefined) {
        if (openProp !== next) onOpenChange?.(next)
        return
      }
      if (openRef.current === next) return
      openRef.current = next
      setInternalOpen(next)
      onOpenChange?.(next)
    },
    [openProp, onOpenChange],
  )

  const clearTimer = useCallback(() => {
    if (timerRef.current === undefined) return
    window.clearTimeout(timerRef.current)
    timerRef.current = undefined
  }, [])

  const schedule = useCallback(
    (next: boolean, delay: number) => {
      clearTimer()
      if (delay <= 0) {
        setOpenState(next)
        return
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = undefined
        setOpenState(next)
      }, delay)
    },
    [clearTimer, setOpenState],
  )

  useEffect(() => clearTimer, [clearTimer])

  const setWrapperRef = useCallback(
    (node: HTMLSpanElement | null) => {
      wrapperRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  const ownTrigger = children === undefined
  const mode: TooltipTrigger = trigger ?? (ownTrigger ? 'press' : 'hover')

  /*
   * Обёртка — якорь измерений. Её ref привязан раньше, чем запускается этот слой раскладки
   * (обёртка — собственный потомок компонента), поэтому дефекта «панель без якоря на первом
   * рендере», который лечится в Popover, у подсказки нет by construction.
   */
  const updatePosition = useCallback(() => {
    const anchor = wrapperRef.current
    const layer = layerRef.current
    if (!anchor || !layer) return

    const next = computePosition(anchor, layer, placement, align)
    setPosition((prev) => (samePosition(prev, next) ? prev : next))
  }, [placement, align])

  // Замер до отрисовки: непозиционированный кадр на экран не попадает
  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
  }, [open, updatePosition])

  /*
   * Плашка зафиксирована в системе окна и сама за триггером не едет. Подписки живут ровно
   * столько, сколько открыта подсказка: их цена — главный довод против портала, а на экране
   * с двумя десятками иконок тулбара открыта одновременно ровно одна плашка.
   */
  useEffect(() => {
    if (!open) return

    let frame = 0
    const scheduleUpdate = () => {
      if (frame !== 0) return
      frame = requestAnimationFrame(() => {
        frame = 0
        updatePosition()
      })
    }

    // Перехват, а не всплытие: событие scroll не всплывает, а прокручиваться может любой предок
    window.addEventListener('scroll', scheduleUpdate, true)
    window.addEventListener('resize', scheduleUpdate)

    /*
     * Текст подсказки приезжает из справочника уже после открытия, а плашка переносится
     * по словам: без наблюдателя перевёрнутая вверх плашка уползла бы из-под триггера.
     * В jsdom ResizeObserver не реализован — отсутствие API не должно ронять тесты.
     */
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate)
    if (observer) {
      if (layerRef.current) observer.observe(layerRef.current)
      if (wrapperRef.current) observer.observe(wrapperRef.current)
    }

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', scheduleUpdate, true)
      window.removeEventListener('resize', scheduleUpdate)
      observer?.disconnect()
    }
  }, [open, updatePosition])

  /*
   * Esc гасит подсказку, не трогая курсор и фокус (WCAG 1.4.13). Перехват, а не всплытие:
   * иначе тот же Esc закрыл бы заодно модалку, внутри которой висит подсказка.
   *
   * Всплытие гасится только в неуправляемом режиме. В управляемом закрытие — просьба
   * к потребителю, и если он держит open, Esc не должен пропадать: иначе модалка
   * с подсказкой внутри перестала бы закрываться совсем.
   */
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (openProp === undefined) event.stopPropagation()
      clearTimer()
      setOpenState(false)
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [open, openProp, clearTimer, setOpenState])

  /*
   * Касание мимо закрывает только режим нажатия: в режиме наведения этим заняты
   * pointerleave и потеря фокуса, а слушатель на каждую иконку тулбара не нужен.
   * «Внутри» — обёртка и плашка: после переезда в портал плашка перестала быть потомком
   * обёртки, и без второй проверки нажатие по ней читалось бы как касание мимо.
   */
  useEffect(() => {
    if (!open || mode !== 'press') return
    const handlePointerDown = (event: Event) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (wrapperRef.current?.contains(target) === true) return
      if (layerRef.current?.contains(target) === true) return
      clearTimer()
      setOpenState(false)
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [open, mode, clearTimer, setOpenState])

  /*
   * Фокус, пришедший сразу за нажатием указателя (клик мышью, тап пальцем в WebView),
   * подсказку не открывает: :focus-visible на планке Safari 15.4 использовать нельзя,
   * поэтому источник фокуса запоминается вручную и сбрасывается на следующем клике.
   */
  const pointerFocusRef = useRef(false)

  const handlePointerDownCapture = (event: ReactPointerEvent<HTMLSpanElement>) => {
    rest.onPointerDownCapture?.(event)
    pointerFocusRef.current = true
  }

  const handlePointerEnter = (event: ReactPointerEvent<HTMLSpanElement>) => {
    rest.onPointerEnter?.(event)
    // pointerType, а не isTouchDevice(): на гибриде мышь и палец работают в одном сеансе
    if (mode !== 'hover' || event.pointerType === 'touch') return
    schedule(true, openDelay)
  }

  const handlePointerLeave = (event: ReactPointerEvent<HTMLSpanElement>) => {
    rest.onPointerLeave?.(event)
    if (mode !== 'hover' || event.pointerType === 'touch') return
    schedule(false, closeDelay)
  }

  /*
   * Курсор, переведённый на плашку, её не гасит (WCAG 1.4.13, hoverable). Раньше это делала
   * сама разметка — плашка лежала внутри обёртки; в портале она обёртке не потомок, поэтому
   * отложенное закрытие снимается явно. Обратный путь (с плашки на триггер) симметричен:
   * его ловит pointerenter обёртки.
   */
  const handleLayerPointerEnter = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (mode !== 'hover' || event.pointerType === 'touch') return
    clearTimer()
  }

  const handleLayerPointerLeave = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (mode !== 'hover' || event.pointerType === 'touch') return
    schedule(false, closeDelay)
  }

  const handleFocus = (event: ReactFocusEvent<HTMLSpanElement>) => {
    rest.onFocus?.(event)
    if (mode !== 'hover') return
    if (pointerFocusRef.current) return
    // Фокус с клавиатуры случайным не бывает — задержку не выдерживаем
    clearTimer()
    setOpenState(true)
  }

  const handleBlur = (event: ReactFocusEvent<HTMLSpanElement>) => {
    rest.onBlur?.(event)
    if (event.currentTarget.contains(event.relatedTarget)) return
    clearTimer()
    setOpenState(false)
  }

  const handleClick = (event: ReactMouseEvent<HTMLSpanElement>) => {
    rest.onClick?.(event)
    pointerFocusRef.current = false
    if (mode !== 'press') return
    clearTimer()
    setOpenState(!open)
  }

  // isFilled — общая проверка слота: ловит и false от идиомы прав can(...) && <Node/>,
  // и пустую строку, которой справочник отвечает на отсутствующий термин
  const hasContent = isFilled(content)

  // Пустой кружок «?» без текста — визуальный мусор; внешний триггер живёт своей жизнью
  if (!hasContent) {
    if (ownTrigger) return null
    return (
      <span {...rest} ref={setWrapperRef} className={cx(s.root, className)} data-state="closed">
        {children}
      </span>
    )
  }

  const describedBy = decorative ? undefined : tooltipId

  const triggerNode = ownTrigger ? (
    <button
      type="button"
      className={cx(s.hint, open && s.hintOpen)}
      aria-label={label}
      aria-describedby={describedBy}
    />
  ) : isValidElement<TriggerProps>(children) ? (
    // Единственное, что навязывается чужому элементу: обработчики и ref живут на обёртке
    cloneElement(children, {
      'aria-describedby': cx(children.props['aria-describedby'], describedBy) || undefined,
    })
  ) : (
    children
  )

  /*
   * Пока замера нет — сторона желаемая. Замер с другой оси (потребитель сменил placement
   * с вертикальной стороны на боковую) тоже не в счёт: зазор-мост живёт отступом слоя,
   * и класс обязан стоять по нужной оси уже к моменту измерения, иначе первый кадр
   * промахнулся бы ровно на ширину зазора.
   */
  const measured =
    position !== null && (position.side === placement || position.side === OPPOSITE[placement])
  const side = measured ? position.side : placement

  const layerStyle: LayerStyle = position
    ? { top: position.top, left: position.left, '--tooltip-arrow-offset': `${position.arrow}px` }
    : // Замер ещё не сделан (подсказка закрыта): плашка скрыта и вёрстку не двигает
      { top: 0, left: 0 }

  return (
    <>
      <span
        {...rest}
        ref={setWrapperRef}
        className={cx(s.root, className)}
        data-state={open ? 'open' : 'closed'}
        data-placement={placement}
        data-size={size}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onPointerDownCapture={handlePointerDownCapture}
        onClick={handleClick}
      >
        {triggerNode}
      </span>

      {/*
       * Портал в body: любой предок с overflow обрезал бы плашку, а backdrop-filter стеклянной
       * панели ломает fixed внутри себя. Соседом обёртки, а не её потомком, — тогда переход
       * курсора на плашку даёт честный pointerleave обёртки, и оба перехода ловятся явно.
       */}
      {createPortal(
        <span
          data-theme={portalTheme ?? undefined}
          ref={layerRef}
          id={tooltipId}
          role={decorative ? undefined : 'tooltip'}
          aria-hidden={decorative || undefined}
          className={cx(s.layer, PLACEMENT_CLASS[side], open && s.layerOpen)}
          style={layerStyle}
          data-state={open ? 'open' : 'closed'}
          data-side={side}
          data-align={align}
          onPointerEnter={handleLayerPointerEnter}
          onPointerLeave={handleLayerPointerLeave}
        >
          <span className={cx(s.bubble, SIZE_CLASS[size])}>
            {title === undefined ? null : <span className={s.title}>{title}</span>}
            <span className={cx(s.text, title !== undefined && s.textMuted)}>{content}</span>
            <span className={s.arrow} />
          </span>
        </span>,
        document.body,
      )}
    </>
  )
}
